import { Badge } from 'primereact/badge'
import { Menubar, type MenubarProps } from 'primereact/menubar'
import { useEffect } from 'react'
import { useQuery } from 'react-query'
import { Outlet, useNavigate } from 'react-router-dom'
import { useNotifications } from '~/hooks/activitiesApi'
import { useUserStore } from '~/stores/userStore'
import type { User } from '~/types/user.types'
import httpClient from '~/utils/httpClient'
import FullScreenLoading from './FullScreenLoadingSpinner'

const AppLayout = () => {
  const navigate = useNavigate()
  const userStore = useUserStore()

  const { data: unreadNotifications } = useNotifications({ unreadOnly: true, getAll: true })
  const unreadCount = unreadNotifications?.length || 0

  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const result = await httpClient.get<{ data: User }>('/api/auth/profile')
      return result
    },
    onError: () => {
      userStore.logout()
      navigate('/auth/login')
    },
    retry: false,
    refetchInterval: 25 * 60 * 1000,
  })
  const menuItems: MenubarProps['model'] = [
    {
      icon: 'pi pi-book',
      label: 'Controls',
      command: () => navigate('/controls'),
    },
    {
      icon: 'pi pi-book',
      label: 'Documents',
      command: () => navigate('/documents'),
    },
    {
      icon: 'pi pi-users',
      label: 'Users',
      command: () => navigate('/users'),
    },
    {
      label: userStore.user ? userStore.user.name : '<My Account>',
      className:
        'min-[960px]:ml-auto [&>.p-menuitem-content]:!bg-transparent [&>.p-menuitem-content]:hover:!bg-transparent [&>.p-menuitem-content]:focus:!bg-transparent',
      template: () =>
        !userStore.user ? null : (
          <div className='mr-2 py-0 px-4 flex items-center gap-2 cursor-default'>
            <span className='pi pi-menuitem-icon pi-user text-lg' />
            <p>
              {userStore.user.name}
              <br />
              <span className='text-sm text-gray-500'>{userStore.user.email}</span>
            </p>
          </div>
        ),
    },
    {
      template: () => (
        <a
          className='p-menuitem-link flex items-center gap-2 cursor-pointer no-underline'
          onClick={(e) => {
            e.preventDefault()
            navigate('/notifications')
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              navigate('/notifications')
            }
          }}
          href='/notifications'
        >
          <span className='pi pi-bell p-overlay-badge text-xl'>
            {unreadCount > 0 && (
              <Badge value={unreadCount} severity='danger' className='p-1 text-xs' />
            )}
          </span>
          <span className='min-[960px]:hidden'>Notifications</span>
        </a>
      ),
    },
    {
      template: () => (
        <button
          type='button'
          className='p-menuitem-link flex items-center gap-2 cursor-pointer no-underline'
          onClick={() => navigate('/auth/logout')}
        >
          <span className='pi pi-sign-out text-xl' />
          <span className='min-[960px]:hidden'>Sign Out</span>
        </button>
      ),
    },
  ]

  useEffect(() => {
    if (profileQuery.data?.data?.data) {
      userStore.setUser(profileQuery.data.data.data)
    }
  }, [profileQuery.data?.data?.data, userStore.setUser])
  if (!profileQuery.isFetched || profileQuery.isLoadingError || profileQuery.error)
    return <FullScreenLoading message='Contacting the server...' />
  return (
    <div className='w-full h-full p-4 flex flex-col'>
      <Menubar model={menuItems} pt={{ menu: { className: 'w-full' } }} />
      <div className='py-4'>
        <Outlet />
      </div>
    </div>
  )
}

export default AppLayout
